import {
    type MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

interface UseHorizontalTableScrollResult {
    containerRef: MutableRefObject<HTMLDivElement | null>;
    hasHorizontalOverflow: boolean;
    isAtScrollStart: boolean;
    isAtScrollEnd: boolean;
    handleScroll: () => void;
    refresh: () => void;
}

/**
 * Tracks horizontal overflow and scroll-edge state for table containers.
 * Useful for showing left/right fade indicators on horizontally scrollable tables.
 */
export const useHorizontalTableScroll = (): UseHorizontalTableScrollResult => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
    const [isAtScrollStart, setIsAtScrollStart] = useState(true);
    const [isAtScrollEnd, setIsAtScrollEnd] = useState(true);

    const refresh = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const canScroll = container.scrollWidth > container.clientWidth + 1;
        const maxLeft = container.scrollWidth - container.clientWidth;
        const left = container.scrollLeft;
        const atStart = left <= 1;
        const atEnd = left >= maxLeft - 1;

        setHasHorizontalOverflow(canScroll);
        setIsAtScrollStart(canScroll ? atStart : true);
        setIsAtScrollEnd(canScroll ? atEnd : true);
    }, []);

    const handleScroll = useCallback(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        refresh();

        const resizeObserver = new ResizeObserver(() => {
            refresh();
        });
        resizeObserver.observe(container);

        const tableElement = container.querySelector('table');
        if (tableElement) {
            resizeObserver.observe(tableElement);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [refresh]);

    return {
        containerRef,
        hasHorizontalOverflow,
        isAtScrollStart,
        isAtScrollEnd,
        handleScroll,
        refresh,
    };
};
